import { type RouteRecordRaw, RouterView } from 'vue-router';
import { isLoading, preventLoginMagicLinkIfAuthenticated, isNotAuthenticated } from '@/router/guards';
import { augWindow } from '@/plugins/context';
import { store, restore } from '@/router/middlewares';
import { LEGACY_MODE_OF_SEND } from '@/dto/sending-methods/SendingMethods.dto';

export const publicRoutes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    redirect: {
      name: 'Launcher',
    },
  },

  {
    path: '/check',
    name: 'Check',
    component: () => import('@/views/public/Check.vue'),
    beforeEnter: store(augWindow.ctx),
  },

  {
    path: '/restore',
    name: 'Restore',
    redirect: restore(augWindow.ctx),
  },

  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/public/Login.vue'),
    beforeEnter: [isLoading, isNotAuthenticated],
  },

  {
    path: '/magic-link/login',
    name: 'MagicLinkLogin',
    component: () => import('@/views/public/MagicLinkLogin.vue'),
    beforeEnter: [isLoading, preventLoginMagicLinkIfAuthenticated],
  },

  {
    path: '/org-signups/first-step',
    name: 'OrgSignups',
    component: () => import('@/views/public/org-signups/Employee.org-signups.vue'),
    children: [
      {
        path: 'error',
        name: 'OrgSignupsError',
        meta: { fullscreen: true },
        component: () => import('@/views/public/org-signups/Error.org-signups.vue'),
      },
    ],
  },

  {
    path: '/spl-signups/first-step',
    name: 'SplSignupsFirstStep',
    component: () => import('@/views/public/spl-signups/Company.spl-signups.vue'),
    children: [
      {
        path: 'success',
        name: 'SplSignupsFirstStepSuccess',
        meta: { fullscreen: true },
        component: () => import('@/views/public/spl-signups/CompanySuccess.spl-signups.vue'),
      },
      {
        path: 'error',
        name: 'SplSignupsFirstStepError',
        meta: { fullscreen: true },
        component: () => import('@/views/public/spl-signups/Error.spl-signups.vue'),
      },
    ],
  },

  {
    path: '/spl-signups/second-step',
    name: 'SplSignupsSecondStep',
    component: () => import('@/views/public/spl-signups/Employee.spl-signups.vue'),
    children: [
      {
        path: 'error',
        name: 'SplSignupsSecondStepError',
        meta: { fullscreen: true },
        component: () => import('@/views/public/spl-signups/Error.spl-signups.vue'),
      },
    ],
  },

  {
    path: '/signup/:signupId',
    redirect: { name: 'EmployeesSignups' },
  },
  {
    path: '/employees-signups/:signupId',
    name: 'EmployeesSignups',
    props: true,
    component: () => import('@/views/public/employees-signups/EmployeesSignups.vue'),
    beforeEnter: [isLoading],
    children: [
      {
        path: 'error',
        name: 'EmployeesSignupsError',
        meta: { fullscreen: true },
        component: () => import('@/views/public/employees-signups/Error.employees-signups.vue'),
      },
    ],
  },

  /**
   * Fix temporaneo:
   * Le vecchie email per notificare SPL dei vari token portavano ad un URL inutilizzato
   * `/landing`. Per retrocompatibilita' viene fatto un redirect alla pagina corretta.
   *
   * @note Le email sono state fixate ma essendo periodiche (una volta al mese) bisogna
   * tenere questo fix fino almeno il 01/01/2024
   */
  {
    path: '/landing',
    redirect: {
      name: 'NotificationsCenter',
      query: {
        email: 1,
      },
    },
  },

  {
    path: '/guest',
    component: RouterView,
    children: [
      {
        name: 'GuestCommunicationAl',
        path: 'communications-al',
        component: () => import('@/views/public/Communication.vue'),
        props: { modeOfSend: LEGACY_MODE_OF_SEND.CORRISPONDENZA_AL },
      },
      {
        name: 'GuestCommunicationEmail',
        path: 'email',
        component: () => import('@/views/public/Communication.vue'),
        props: { modeOfSend: LEGACY_MODE_OF_SEND.EMAIL },
      },
      {
        name: 'GuestCommunicationEmailCondominiale',
        path: 'email-condominiale',
        component: () => import('@/views/public/Communication.vue'),
        props: { modeOfSend: LEGACY_MODE_OF_SEND.EMAIL_CONDOMINIALE },
      },
      {
        name: 'GuestOrderAcceptance',
        path: 'orders',
        component: () => import('@/views/public/guest/orders/OrderAcceptance.vue'),
      },
      {
        name: 'GuestConsultancies',
        path: 'consultancies',
        component: () => import('@/views/public/guest/consultancies/Consultancies.vue'),
      },
      {
        name: 'GuestConsultanciesSharedLinkCustomPath',
        path: 'consultancies/:sharedLinkCustomPath+',
        component: () => import('@/views/public/guest/consultancies/Consultancies.vue'),
      },
    ],
  },

  {
    path: '/sicura-solution',
    name: 'SicuraSolutionHome',
    children: [
      {
        path: '',
        name: 'SicuraSolutionEntryPoint',
        redirect: { name: 'SicuraSolutionLauncher' },
      },
      {
        path: 'check',
        name: 'SicuraSolutionCheck',
        component: () => import('@/views/public/Check.vue'),
        beforeEnter: store(augWindow.ctx),
      },

      {
        path: 'restore',
        name: 'SicuraSolutionRestore',
        redirect: restore(augWindow.ctx),
      },

      {
        path: 'login',
        name: 'SicuraSolutionLogin',
        component: () => import('@/views/public/Login.vue'),
        beforeEnter: [isLoading, isNotAuthenticated],
      },

      {
        path: 'magic-link/login',
        name: 'SicuraSolutionMagicLinkLogin',
        component: () => import('@/views/public/MagicLinkLogin.vue'),
        beforeEnter: [isLoading, preventLoginMagicLinkIfAuthenticated],
      },
    ],
  },
];
